// DEFCAT-R2-02 — Lock contention dialog (policy OQ-R2-8).
//
// Surfaces when `POST .../lock/acquire` returns 409 `lock_contention`
// (a foreign user holds the live edit lock). Replaces the previous
// silent console noise + read-only banner-only behaviour: the analyst
// who tried to claim now gets an explicit, audited choice.
//
// Two paths (per the OQ-R2-8 decision — NO silent release, NO TTL):
//   - View         — open the data set read-only (dismiss; the page
//                    stays in the contended/read-only state).
//   - Claim & Edit — expand a REQUIRED reason field (10–500 chars,
//                    enforced client-side) and re-POST acquire with
//                    `takeover: { reason }`. On 200 the holder is
//                    displaced (audited server-side) and editing
//                    proceeds exactly as a normal claim.
//
// The component is presentational: it owns the reason text + the
// expand/collapse of the takeover panel, but the acquire/release side
// effects live in `useLockEffect`. `onClaim(reason)` and `onView` are
// injected so the dialog stays trivially unit-testable.
import { useId, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { LockIcon } from "@aeriosiq/ui/icons";
export const REASON_MIN_LENGTH = 10;
export const REASON_MAX_LENGTH = 500;
export const REASON_HELPER_TEXT = "10 characters minimum";

export interface LockContentionDialogProps {
  open: boolean;
  /** Display name of the user currently holding the lock. */
  holderName: string;
  /** ISO timestamp the foreign holder acquired / last held the lock. */
  heldSince: string | null;
  /** True while a takeover acquire is in flight (disables the controls). */
  claiming?: boolean;
  /** Non-null when the last takeover attempt failed (e.g. 400 short reason). */
  claimError?: string | null;
  /** Open the data set read-only. */
  onView: () => void;
  /** Re-issue acquire with `takeover: { reason }`. */
  onClaim: (reason: string) => void;
  /** Dismiss without viewing or claiming (navigates back upstream). */
  onCancel: () => void;
}

function heldSinceLabel(iso: string | null): string {
  if (!iso) return "just now";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "just now";
  const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (deltaSec < 60) return "just now";
  const min = Math.floor(deltaSec / 60);
  if (min < 60) return `${String(min)} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${String(hr)} h ago`;
  return new Date(iso).toLocaleDateString();
}

export function LockContentionDialog({
  open,
  holderName,
  heldSince,
  claiming = false,
  claimError = null,
  onView,
  onClaim,
  onCancel,
}: LockContentionDialogProps): React.ReactElement {
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const [reason, setReason] = useState("");
  const reasonFieldId = useId();

  const trimmedLength = reason.trim().length;
  const reasonValid =
    trimmedLength >= REASON_MIN_LENGTH && reason.length <= REASON_MAX_LENGTH;
  const tooLong = reason.length > REASON_MAX_LENGTH;

  const handleClaimClick = (): void => {
    if (!takeoverOpen) {
      // First click expands the reason panel; the analyst confirms with
      // a second click once a valid reason is entered.
      setTakeoverOpen(true);
      return;
    }
    if (!reasonValid) return;
    onClaim(reason.trim());
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby={`${reasonFieldId}-title`}
      maxWidth="xs"
      fullWidth
      data-testid="lock-contention-dialog"
    >
      <DialogTitle
        id={`${reasonFieldId}-title`}
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <LockIcon fontSize="small" aria-hidden />
        Data set locked
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Locked by <strong>{holderName}</strong> (since{" "}
          {heldSinceLabel(heldSince)}).
        </DialogContentText>
        <Collapse in={takeoverOpen} unmountOnExit>
          <Stack spacing={1} sx={{ mt: 2 }}>
            <DialogContentText variant="body2">
              Claiming displaces {holderName}. A reason is required and is
              written to the audit log.
            </DialogContentText>
            <TextField
              id={reasonFieldId}
              label="Reason for takeover"
              placeholder="Why are you taking over this data set?"
              multiline
              minRows={2}
              fullWidth
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              disabled={claiming}
              required
              error={tooLong}
              helperText={
                tooLong
                  ? `${String(reason.length)} / ${String(REASON_MAX_LENGTH)} — too long`
                  : REASON_HELPER_TEXT
              }
              slotProps={{
                htmlInput: { "data-testid": "lock-takeover-reason" },
              }}
            />
            {claimError ? (
              <Alert severity="error" data-testid="lock-takeover-error">
                {claimError}
              </Alert>
            ) : null}
          </Stack>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={claiming} color="inherit">
          Cancel
        </Button>
        <Button onClick={onView} disabled={claiming}>
          View
        </Button>
        <Button
          onClick={handleClaimClick}
          variant="contained"
          // Once the takeover panel is open the confirm button stays
          // disabled until the reason clears the 10-char gate.
          disabled={claiming || (takeoverOpen && !reasonValid)}
          data-testid="lock-claim-edit"
        >
          {takeoverOpen ? "Confirm takeover" : "Claim & Edit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
